import { useCallback, useEffect, useRef } from 'react';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { useRegistrationAPI } from '@/hooks/registration/useRegistrationApi';
import {
    PaginationModel,
    REGISTRATION_MODE,
    SortModel,
    TAB_TYPE,
} from '@/types/pages/storeManagement/registration/registrationTypes';
import { useStoreDetails } from '@/mobile/hooks/useStoreDetails';

const POLLING_INTERVAL_MS = 10000;

const DEFAULT_PAGINATION_MODEL: PaginationModel = {
    page: 0,
    pageSize: 5,
};

const DEFAULT_SORT_MODEL: SortModel = {
    field: null,
    sort: null,
};

export type RefreshTableOptions = {
    tab?: TAB_TYPE;
    resetTable?: boolean;
    paginationModel?: PaginationModel;
    sortModel?: SortModel;
    searchMac?: string;
    clearSelected?: boolean;
};

export type RefreshRegistrationOptions = {
    resetTable?: boolean;
    clearSelected?: boolean;
};

export type RefreshRegistrationInternalOptions = {
    strategy: 'initial' | 'preserve-current';
    signal?: AbortSignal;
    resetTable?: boolean;
    clearSelected?: boolean;
};

const useRegistrationLifecycle = () => {
    const { manageStoreId } = useAppContext();
    const { state, dispatch } = useRegistrationState();
    const { fetchAllDevices, fetchInfraDevicesTableData } = useRegistrationAPI();
    const { fetchStoreDetails, formatUtcToStoreTime } = useStoreDetails();

    const stateRef = useRef(state);
    const fetchStoreDetailsRef = useRef(fetchStoreDetails);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollingAbortRef = useRef<AbortController | null>(null);
    const interactionAbortRef = useRef<AbortController | null>(null);
    const operationAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        fetchStoreDetailsRef.current = fetchStoreDetails;
    }, [fetchStoreDetails]);

    const clearPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        pollingAbortRef.current?.abort();
        pollingAbortRef.current = null;
    }, []);

    const clearSelectedDevices = useCallback(() => {
        dispatch({
            type: 'SET_TABLE_STATE',
            payload: {
                selectedInfraDevices: [],
            },
        });
    }, [dispatch]);

    const resetTableState = useCallback(
        ({
            paginationModel = DEFAULT_PAGINATION_MODEL,
            sortModel = DEFAULT_SORT_MODEL,
        }: {
            paginationModel?: PaginationModel;
            sortModel?: SortModel;
        } = {}) => {
            dispatch({
                type: 'SET_TABLE_STATE',
                payload: {
                    status: 'loading',
                    selectedInfraDevices: [],
                    infraDeviceInfo: [],
                    sortModel,
                    paginationModel,
                    rowCount: 0,
                },
            });

            dispatch({
                type: 'SET_SEARCH_MAC',
                payload: '',
            });
        },
        [dispatch],
    );

    const refreshTable = useCallback(
        async ({
            tab = stateRef.current.currentTab,
            resetTable = false,
            clearSelected = false,
            paginationModel = stateRef.current.deviceInfoState.paginationModel,
            sortModel = stateRef.current.deviceInfoState.sortModel,
            searchMac = stateRef.current.searchMac,
        }: RefreshTableOptions = {}) => {
            if (!manageStoreId) return;
            if (tab === TAB_TYPE.NONE) return;

            interactionAbortRef.current?.abort();
            interactionAbortRef.current = new AbortController();

            const { signal } = interactionAbortRef.current;

            const nextPaginationModel = resetTable ? DEFAULT_PAGINATION_MODEL : paginationModel;
            const nextSortModel = resetTable ? DEFAULT_SORT_MODEL : sortModel;
            const nextSearchMac = resetTable ? '' : searchMac;

            if (resetTable) {
                resetTableState({
                    paginationModel: nextPaginationModel,
                    sortModel: nextSortModel,
                });
            } else if (clearSelected) {
                clearSelectedDevices();
            }

            await fetchInfraDevicesTableData({
                signal,
                tab,
                paginationModel: nextPaginationModel,
                sortModel: nextSortModel,
                searchMac: nextSearchMac,
                infraDevice: stateRef.current.infraDevice,
                registrationMode: stateRef.current.registrationMode,
            });
        },
        [fetchInfraDevicesTableData, manageStoreId, resetTableState, clearSelectedDevices],
    );

    const refreshRegistration = useCallback(
        async ({ strategy, signal, resetTable = false, clearSelected = false }: RefreshRegistrationInternalOptions) => {
            if (!manageStoreId) return false;

            const currentTab = stateRef.current.currentTab;

            const allDevicesResult = await fetchAllDevices({
                signal,
                currentTab,
                strategy,
            });

            if (signal?.aborted || !allDevicesResult) {
                return false;
            }

            const { effectiveTab, infraDevice, installationMode } = allDevicesResult;

            if (effectiveTab === TAB_TYPE.NONE) {
                dispatch({
                    type: 'TOGGLE_LOADING',
                    payload: {
                        key: 'storeStateLoading',
                        isLoading: false,
                    },
                });
                if (clearSelected) {
                    clearSelectedDevices();
                }

                return installationMode === REGISTRATION_MODE.INSTALLATION_MODE;
            }

            const shouldResetTable = resetTable || effectiveTab !== currentTab;

            const paginationModel = shouldResetTable
                ? DEFAULT_PAGINATION_MODEL
                : stateRef.current.deviceInfoState.paginationModel;

            const sortModel = shouldResetTable ? DEFAULT_SORT_MODEL : stateRef.current.deviceInfoState.sortModel;

            const searchMac = shouldResetTable ? '' : stateRef.current.searchMac;

            if (shouldResetTable) {
                resetTableState({
                    paginationModel,
                    sortModel,
                });
            } else if (clearSelected) {
                clearSelectedDevices();
            }

            await fetchInfraDevicesTableData({
                signal,
                tab: effectiveTab,
                paginationModel,
                sortModel,
                searchMac,
                infraDevice,
                registrationMode: installationMode,
            });

            if (signal?.aborted) {
                return false;
            }

            dispatch({
                type: 'TOGGLE_LOADING',
                payload: {
                    key: 'storeStateLoading',
                    isLoading: false,
                },
            });

            return installationMode === REGISTRATION_MODE.INSTALLATION_MODE;
        },
        [dispatch, fetchAllDevices, fetchInfraDevicesTableData, manageStoreId, resetTableState],
    );

    const refreshRegistrationRef = useRef(refreshRegistration);

    useEffect(() => {
        refreshRegistrationRef.current = refreshRegistration;
    }, [refreshRegistration]);

    const startPolling = useCallback(() => {
        clearPolling();

        pollingAbortRef.current = new AbortController();
        const { signal } = pollingAbortRef.current;

        intervalRef.current = setInterval(() => {
            void (async () => {
                const shouldContinuePolling = await refreshRegistrationRef.current({
                    strategy: 'preserve-current',
                    signal,
                    resetTable: false,
                });

                if (signal.aborted) return;

                if (!shouldContinuePolling) {
                    clearPolling();
                }
            })();
        }, POLLING_INTERVAL_MS);
    }, [clearPolling]);

    const refreshRegistrationNow = useCallback(
        async ({ resetTable = false, clearSelected = false }: RefreshRegistrationOptions = {}) => {
            clearPolling();

            operationAbortRef.current?.abort();
            operationAbortRef.current = new AbortController();

            const { signal } = operationAbortRef.current;

            const shouldContinuePolling = await refreshRegistrationRef.current({
                strategy: 'preserve-current',
                signal,
                resetTable,
                clearSelected,
            });
            if (!signal.aborted && shouldContinuePolling) {
                startPolling();
            }

            return shouldContinuePolling;
        },
        [clearPolling, startPolling],
    );

    const initialize = useCallback(
        async (signal: AbortSignal) => {
            dispatch({
                type: 'CLEAR',
            });

            dispatch({
                type: 'TOGGLE_LOADING',
                payload: {
                    key: 'storeStateLoading',
                    isLoading: true,
                },
            });

            const response = await fetchStoreDetailsRef.current();

            if (signal.aborted) {
                return;
            }

            if (!response) {
                dispatch({
                    type: 'TOGGLE_LOADING',
                    payload: {
                        key: 'storeStateLoading',
                        isLoading: false,
                    },
                });
                return;
            }

            dispatch({
                type: 'SET_STORE_STATUS',
                payload: response.store_info.installation_mode,
            });

            dispatch({
                type: 'SET_STORE_DEVICE_TOTAL',
                payload: response.store_info.devices_count.total,
            });

            const shouldContinuePolling = await refreshRegistrationRef.current({
                strategy: 'initial',
                signal,
                resetTable: true,
            });

            if (!signal.aborted && shouldContinuePolling) {
                startPolling();
            }
        },
        [dispatch, startPolling],
    );

    useEffect(() => {
        if (!manageStoreId) {
            return;
        }

        clearPolling();

        pollingAbortRef.current = new AbortController();
        const { signal } = pollingAbortRef.current;

        void initialize(signal);

        return () => {
            clearPolling();

            interactionAbortRef.current?.abort();
            interactionAbortRef.current = null;

            operationAbortRef.current?.abort();
            operationAbortRef.current = null;
        };
    }, [manageStoreId, initialize, clearPolling]);

    return {
        formatUtcToStoreTime,
        refreshTable,
        refreshRegistrationNow,
    };
};

export default useRegistrationLifecycle;
